const isNonEmptyArray = (v) => Array.isArray(v) && v.length > 0;

export const hasPrescriptions = (treatment = {}) => {
  if (!treatment) return false;

  // generic fields
  if (isNonEmptyArray(treatment.prescriptions)) return true;
  if (isNonEmptyArray(treatment.medications)) return true;
  if (treatment.prescription) return true;

  // allergic-rhinitis specific prescription fields
  const prescriptionFields = [
    "firstLineTherapyPrescriptions",
    "stepUpTherapyPrescriptions",
    "alternativeFirstLineTherapyPrescriptions",
    "addOnTherapyOralDecongestantsPrescriptions",
    "addOnTherapyIntranasalDecongestantsPrescriptions",
    "addOnTherapyOralAntihistaminesPrescriptions",
    "nasalSalineRinsePrescriptions",
    "nasalSalineSprayPrescriptions",
    "nasalLubricantPrescriptions",
    "addOnTherapyPrescriptions",
  ];

  for (const f of prescriptionFields) {
    if (isNonEmptyArray(treatment[f])) return true;
  }

  return false;
};

export const hasPersonalGoals = (goals = {}) => {
  if (Array.isArray(goals)) {
    return goals.some(
      (g) => g && (g.type === "personal" || g.category === "personal" || g.personal === true)
    );
  }
  if (goals && Array.isArray(goals.personalGoals)) return goals.personalGoals.length > 0;
  if (typeof goals.personalGoals === "string" && goals.personalGoals.trim()) return true;
  return false;
};

export const hasHowToReach = (goals = {}) => {
  if (Array.isArray(goals)) {
    return goals.some(
      (g) => g && (g.type === "howToReach" || g.category === "howToReach" || g.howToReach === true)
    );
  }
  if (goals && Array.isArray(goals.howToAchieveGoals)) return goals.howToAchieveGoals.length > 0;
  if (typeof goals.howToAchieveGoals === "string" && goals.howToAchieveGoals.trim()) return true;
  return false;
};

const readRuntimeOverride = () => {
  try {
    if (typeof window === "undefined") return null;
    const ls = window.localStorage && window.localStorage.getItem("hc_show_vertical");
    if (ls === "1" || ls === "true") return true;
    if (ls === "0" || ls === "false") return false;
    // global toggle for quick debugging
    if (typeof window.__HC_SHOW_VERTICAL !== "undefined") return !!window.__HC_SHOW_VERTICAL;
    // check URL query param: ?verticalLayout=true
    const qp = new URLSearchParams(window.location.search).get("verticalLayout");
    if (qp === "1" || qp === "true") return true;
    if (qp === "0" || qp === "false") return false;
    return null;
  } catch (e) {
    return null;
  }
};

export const hasFollowUp = (documentation = {}, treatment = {}) => {
  const edu = (documentation && documentation.education) || (treatment && treatment.education) || documentation || {};
  // followUpDate or followUp flag or appointmentBooked indicate a follow-up
  if (edu?.followUpDate) return true;
  if (edu?.followUp) return true;
  if (edu?.appointmentBooked) return true;
  return false;
};

const shouldShowVertical = ({ documentation, treatment, goals } = {}) => {
  const override = readRuntimeOverride();
  if (override === true) return true;
  if (override === false) return false;

  // default vertical when there's no prescriptions/goals/how-to-reach
  const defaultVertical = !hasPrescriptions(treatment) && !hasPersonalGoals(goals) && !hasHowToReach(goals);

  // New rule: if there is a follow-up and either prescriptions are missing or personal goals are missing,
  // ensure vertical layout so follow-up can be shown below the vertical card.
  if (hasFollowUp(documentation, treatment) && (!hasPrescriptions(treatment) || !hasPersonalGoals(goals))) {
    return true;
  }

  return defaultVertical;
};

export default shouldShowVertical;
